/**
 * Filter helpers for Assignment → Time Off history.
 */

import {isInHistoryYear} from './historyYears'
import type {TimeOffHistoryEntry, TimeOffHistoryFilters} from './types'

const parseIso = (iso: string): Date => {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, (month || 1) - 1, day || 1)
}

const isWithinDateRange = (
  entry: TimeOffHistoryEntry,
  range: TimeOffHistoryFilters['dateRange'],
  today = new Date(),
): boolean => {
  if (range === 'all') return true
  const eventDate = parseIso(entry.dateIso)
  if (range === 'last30') {
    const cutoff = new Date(today)
    cutoff.setDate(cutoff.getDate() - 30)
    return eventDate >= cutoff
  }
  // Year to date
  const startOfYear = new Date(today.getFullYear(), 0, 1)
  return eventDate >= startOfYear
}

const matchesSearch = (entry: TimeOffHistoryEntry, search: string): boolean => {
  const query = search.trim().toLowerCase()
  if (!query) return true
  const formattedPolicy = formatActivityPolicy(entry.policyLabel, entry.typeName).toLowerCase()
  return (
    entry.activity.toLowerCase().includes(query) ||
    entry.policyLabel.toLowerCase().includes(query) ||
    formattedPolicy.includes(query) ||
    entry.typeName.toLowerCase().includes(query) ||
    entry.status.toLowerCase().includes(query) ||
    (entry.approvedBy ?? '').toLowerCase().includes(query) ||
    (entry.approvedDate ?? '').toLowerCase().includes(query)
  )
}

/**
 * Formats a policy label for clean display in history rows.
 * Strips raw internal request IDs (e.g. "Request 6ac568996d37870d1b141866 · DA Sick leave Policy"
 * → "DA Sick leave Policy", or "Request PTO-1048 · Standard Vacation" → "Standard Vacation").
 */
export const formatActivityPolicy = (
  policyLabel: string | undefined | null,
  typeName?: string,
): string => {
  if (!policyLabel) return typeName || ''
  const trimmed = policyLabel.trim()
  // Pattern: "Request <id> · <PolicyName>" (where <id> is an ObjectId hex, PTO ref, etc.)
  const matchWithPolicy = /^Request\s+\S+\s*·\s*(.+)$/i.exec(trimmed)
  if (matchWithPolicy?.[1]) {
    return matchWithPolicy[1].trim()
  }
  // Pattern: "Request <id>" without policy name attached
  const matchRequestOnly = /^Request\s+\S+\s*$/i.exec(trimmed)
  if (matchRequestOnly) {
    return typeName || 'Request'
  }
  return trimmed
}

/**
 * Apply tab, type, date, status, and free-text search to history rows.
 *
 * The date filter is `filters.year` when it is set (STFFLG-4310, flag timeOffHistoryYears: a year
 * or All, anchored on `hireDateIso` by `isInHistoryYear`, so rows before the hire date fall under
 * the hire year), else the legacy `filters.dateRange` relative to `today`.
 */
export const filterTimeOffHistory = (
  history: TimeOffHistoryEntry[],
  filters: TimeOffHistoryFilters,
  today = new Date(),
  hireDateIso?: string,
): TimeOffHistoryEntry[] =>
  history.filter((entry) => {
    if (filters.tab !== 'All' && !entry.categories.includes(filters.tab)) {
      return false
    }
    if (filters.typeName !== 'all' && entry.typeName !== filters.typeName) {
      return false
    }
    if (filters.status !== 'all' && entry.status !== filters.status) {
      return false
    }
    if (
      filters.year !== undefined
        ? !isInHistoryYear(entry.dateIso, filters.year, hireDateIso)
        : !isWithinDateRange(entry, filters.dateRange, today)
    ) {
      return false
    }
    return matchesSearch(entry, filters.search)
  })

export const formatSignedHours = (value: number | null, sign: 'used' | 'earned'): string => {
  if (value === null || value === undefined) return '—'
  const formatted = Number.isInteger(value) ? String(value.toFixed(2)) : value.toFixed(2)
  // Normalize trailing zeros: 8.00 → 8.00 kept for ledger consistency with mock
  return sign === 'used' ? `-${formatted}` : `+${formatted}`
}

export const formatBalanceHours = (value: number | null): string => {
  if (value === null || value === undefined) return '—'
  return value.toFixed(2)
}

const REQUEST_REF_IN_LABEL = /^Request\s+(\S+)/i

export interface RequestStatusLookup {
  id: string
  status: string
  externalRequestId?: string
  startDateIso?: string
  typeName?: string
}

export const getHistoryEntryRequestKey = (
  entry: TimeOffHistoryEntry,
  requests?: RequestStatusLookup[],
): {key: string | null; matchedRequest?: RequestStatusLookup} => {
  const labelRef = REQUEST_REF_IN_LABEL.exec(entry.policyLabel || '')?.[1]
  const explicitRef = entry.requestRef

  if (requests && requests.length > 0) {
    // An explicit reference always wins, whichever position its request has in the list.
    const matchesRef = (ref: string | undefined) => (r: RequestStatusLookup) =>
      Boolean(ref) && (r.externalRequestId === ref || r.id === ref)
    const byRef = requests.find(matchesRef(explicitRef)) ?? requests.find(matchesRef(labelRef))
    if (byRef) {
      return {key: byRef.id, matchedRequest: byRef}
    }

    // Fallback for request rows that carry no reference: same start day and type, but only when
    // exactly one request fits. Two same-day requests of one type stay separate rows, and ledger
    // rows (accruals, adjustments) never use the fallback, so they can't merge into a request.
    if (!explicitRef && !labelRef && entry.dateIso && entry.categories.includes('Requests')) {
      const sameDayAndType = requests.filter(
        (r) => r.startDateIso?.startsWith(entry.dateIso) && r.typeName === entry.typeName,
      )
      if (sameDayAndType.length === 1) {
        const [only] = sameDayAndType
        return {key: only.id, matchedRequest: only}
      }
    }
  }

  const key = explicitRef ?? labelRef ?? null
  return {key}
}

/**
 * Deduplicate time off history rows so that:
 * - Exactly one record is shown per request if the request was approved, denied, or cancelled.
 * - Prior pending submission records are suppressed once a decision is made.
 * - Requests still in 'Pending' status retain their pending record.
 * - Non-request ledger entries (accruals, manual adjustments, carryover) remain untouched.
 */
export const deduplicateTimeOffHistory = (
  history: TimeOffHistoryEntry[],
  requests?: RequestStatusLookup[],
): TimeOffHistoryEntry[] => {
  if (!history || history.length === 0) return []

  const requestRowsByKey = new Map<
    string,
    Array<{entry: TimeOffHistoryEntry; matchedRequest?: RequestStatusLookup}>
  >()
  const independentRows: TimeOffHistoryEntry[] = []

  for (const entry of history) {
    const {key, matchedRequest} = getHistoryEntryRequestKey(entry, requests)
    if (!key) {
      independentRows.push(entry)
    } else {
      const existing = requestRowsByKey.get(key)
      if (existing) {
        existing.push({entry, matchedRequest})
      } else {
        requestRowsByKey.set(key, [{entry, matchedRequest}])
      }
    }
  }

  const dedupedRequestRows: TimeOffHistoryEntry[] = []

  for (const [key, candidateRows] of requestRowsByKey.entries()) {
    if (candidateRows.length === 1) {
      dedupedRequestRows.push(candidateRows[0].entry)
      continue
    }

    const matchedRequest =
      candidateRows.find((c) => c.matchedRequest)?.matchedRequest ??
      requests?.find((r) => r.id === key || r.externalRequestId === key)
    const targetStatus = matchedRequest?.status

    if (targetStatus && targetStatus !== 'Pending') {
      // Exclude pending submission records
      const nonPending = candidateRows.filter(
        ({entry}) =>
          entry.status !== 'Pending' && !entry.activity.toLowerCase().includes('submitted'),
      )
      const candidates = nonPending.length > 0 ? nonPending : candidateRows

      const matchingStatus = candidates.filter(({entry}) => entry.status === targetStatus)
      if (matchingStatus.length > 0) {
        const preferred =
          matchingStatus.find(({entry}) => entry.usedHours !== null)?.entry ??
          matchingStatus[0].entry
        dedupedRequestRows.push(preferred)
      } else {
        const preferred =
          candidates.find(({entry}) => entry.usedHours !== null)?.entry ?? candidates[0].entry
        dedupedRequestRows.push(preferred)
      }
    } else if (targetStatus === 'Pending') {
      const pendingRow =
        candidateRows.find(({entry}) => entry.status === 'Pending')?.entry ?? candidateRows[0].entry
      dedupedRequestRows.push(pendingRow)
    } else {
      const hasDecided = candidateRows.some(
        ({entry}) =>
          entry.status === 'Approved' || entry.status === 'Denied' || entry.status === 'Cancelled',
      )
      if (hasDecided) {
        const nonPending = candidateRows.filter(
          ({entry}) =>
            entry.status !== 'Pending' && !entry.activity.toLowerCase().includes('submitted'),
        )
        const candidates = nonPending.length > 0 ? nonPending : candidateRows
        const preferred =
          candidates.find(({entry}) => entry.usedHours !== null)?.entry ?? candidates[0].entry
        dedupedRequestRows.push(preferred)
      } else {
        dedupedRequestRows.push(candidateRows[0].entry)
      }
    }
  }

  const combined = [...independentRows, ...dedupedRequestRows]
  combined.sort((a, b) => (b.dateIso || '').localeCompare(a.dateIso || ''))
  return combined
}

/**
 * Splits a balance card's policy line into a label and the value the card bolds:
 * "Current cap: 48 hrs" → {label: 'Current cap:', value: '48 hrs'};
 * "Accrues 3.08 hrs every 2 weeks" → {label: 'Accrues', value: '3.08 hrs every 2 weeks'}.
 * A line with neither shape stays all label.
 */
export function splitPolicyMetaLine(line: string): {label: string; value?: string} {
  const colon = line.indexOf(': ')
  if (colon > 0) {
    return {label: line.slice(0, colon + 1), value: line.slice(colon + 2)}
  }
  const accrues = /^(Accrues)\s+(.+)$/.exec(line)
  if (accrues) {
    return {label: accrues[1], value: accrues[2]}
  }
  return {label: line}
}

const ANNUAL_USAGE_LIMIT_LABEL = 'Annual usage limit:'

/**
 * The meta line for a Flexible or Manual policy's annual usage limit (STFFLG-4313), worded as the
 * msp's `annualUsageLimitLine` words it: the limit in hours rounded to at most 2 decimals with no
 * trailing zeros ("Annual usage limit: 40 hrs", "… 7.5 hrs"), or "Annual usage limit: None" when
 * the policy has no limit.
 */
export function annualUsageLimitLine(limitHours: number | null): string {
  if (limitHours === null) {
    return `${ANNUAL_USAGE_LIMIT_LABEL} None`
  }
  return `${ANNUAL_USAGE_LIMIT_LABEL} ${Number(limitHours.toFixed(2))} hrs`
}

/**
 * The policy lines under a balance card's divider, shared by the admin Assignment → Time Off card
 * and the candidate My Info → Time Off card (STFFLG-4313):
 * - An exact repeat of an earlier line is dropped. An msp before STFFLG-4313 sent a Manual
 *   policy's summary twice ("Balances managed individually" on two rows).
 * - A card that tracks no balance (a Flexible or Manual policy) shows its annual usage limit once:
 *   the msp's own "Annual usage limit:" line when it sends one, else `annualUsageLimitLine`, so
 *   the limit shows on an older msp too and never twice on a current one. Nothing is added when
 *   the response has no `annualUsageLimitHours` field at all.
 * Lines can still repeat across cards, so callers key each row by its index, never its text.
 */
export function balanceCardPolicyLines(
  lines: string[],
  options: {tracksBalance: boolean; annualUsageLimitHours?: number | null},
): string[] {
  const unique = lines.filter((line, index) => lines.indexOf(line) === index)
  const {tracksBalance, annualUsageLimitHours} = options
  if (
    tracksBalance ||
    annualUsageLimitHours === undefined ||
    unique.some((line) => line.startsWith(ANNUAL_USAGE_LIMIT_LABEL))
  ) {
    return unique
  }
  return [...unique, annualUsageLimitLine(annualUsageLimitHours)]
}

/** The year-scoped figures the msp sends on a Flexible or Manual balance card (STFFLG-4313). */
export interface AnnualUsageFigureInput {
  /** L: the policy's annual usage limit in hours; null = no limit. */
  annualUsageLimitHours?: number | null
  /** U: hours of Approved requests on this policy that start in the current UTC year. */
  annualUsedHours?: number | null
  /** P: hours of Pending requests on this policy that start in the current UTC year. */
  annualPendingHours?: number | null
  /** R: max(0, L − U); pending hours are not subtracted. Null when there is no limit. */
  annualRemainingHours?: number | null
}

/** What a Flexible or Manual balance card shows above its divider. */
export interface AnnualUsageFigures {
  /** The headline figure (R to 2 decimals), or null for the '—' of a policy with no limit. */
  value: string | null
  /** The caption beside the headline. */
  caption: string
  /** The line under the headline. */
  usedLine: string
  /** The usage bar's fill, 0–100. */
  barPercent: number
}

/**
 * A Flexible or Manual card's figures (STFFLG-4313), from the msp's year-scoped fields. The year is
 * a request's startDate UTC year, the same year the msp's annual limit check counts; U, P and R
 * are defined on `AnnualUsageFigureInput`.
 * - With a limit: "R hours available", then "U of L hours used this year", plus " · P pending"
 *   when P > 0, and a bar at min(100, round(U / L · 100)), full when L is 0. Admin requests skip
 *   the limit check, so U can pass L: R stays at 0 and the bar at 100.
 * - With no limit: '—' with "no annual limit", then "U hours used this year" (plus pending), and
 *   an empty bar.
 * Returns null when the msp sent no annual figures (an Accrual card, or an msp before
 * STFFLG-4313); the card then keeps its placeholder text.
 */
export function annualUsageFigures(
  card: AnnualUsageFigureInput,
  unit = 'hours',
): AnnualUsageFigures | null {
  const used = card.annualUsedHours
  if (used === null || used === undefined) {
    return null
  }
  const pending = card.annualPendingHours ?? 0
  const pendingSuffix = pending > 0 ? ` · ${pending.toFixed(2)} pending` : ''
  const limit = card.annualUsageLimitHours
  if (limit === null || limit === undefined) {
    return {
      value: null,
      caption: 'no annual limit',
      usedLine: `${used.toFixed(2)} ${unit} used this year${pendingSuffix}`,
      barPercent: 0,
    }
  }
  const remaining = Math.max(0, card.annualRemainingHours ?? limit - used)
  return {
    value: remaining.toFixed(2),
    caption: `${unit} available`,
    usedLine: `${used.toFixed(2)} of ${limit.toFixed(2)} ${unit} used this year${pendingSuffix}`,
    barPercent: limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100,
  }
}
